/**
 * Vault API contract — frontend.
 *
 * MIRROR: keep in sync with functions/contracts/vaultApi.js
 * Any change to a shape or error code MUST update both copies in the same commit.
 *
 * Imported by Lane A Phase 1 frontend (src/pages/Vault.js, src/pages/VaultEntry.js,
 * src/components/vault/*).
 *
 * @typedef {import('./oracleApi').VectorName} VectorName
 * @typedef {import('./oracleApi').Verdict} Verdict
 * @typedef {import('./oracleApi').Recommendation} Recommendation
 *
 * A LOCKED row keeps its thumbnail and nulls every derived reading — the photo
 * is the caller's own and a grid of blank tiles reads as broken rather than as
 * gated. `unlock` carries progress toward opening THAT scan so the grid can
 * show how close the row is; it is null on an open row.
 *
 * @typedef {Object} VaultUnlockProgress
 * @property {"locked"} state
 * @property {number} done                      // judgments given toward THIS scan
 * @property {number} required                  // UNLOCK_FEEDBACK_QUOTA
 * @property {string[]} paths                   // ["trial", "judging"]
 *
 * @typedef {Object} VaultIndexEntry
 * @property {string} scan_id                   // uuid v4
 * @property {string} timestamp                 // ISO 8601, submission time
 * @property {string | null} taken_at           // ISO 8601, user-specified; null until set
 * @property {string} thumbnail_url             // signed, 7-day
 * @property {boolean} locked
 * @property {VaultUnlockProgress | null} unlock // present when locked, else null
 * @property {number | null} overall_score      // int 1-100; null when locked
 * @property {VectorName[]} top_3_improvement_vectors
 *
 * @typedef {Object} ListVaultEntriesResponse
 * @property {"success"} status
 * @property {VaultIndexEntry[]} entries
 * @property {string | null} next_cursor
 *
 * @typedef {Object} VaultEntryDetail
 * @property {"success"} status
 * @property {string} scan_id
 * @property {string} timestamp
 * @property {string} processed_image_url
 * @property {Verdict} verdict
 * @property {Recommendation[]} recommendations
 * @property {string | null} taken_at
 * @property {number | null} learned_percentile  // 0..1 calibrated model score (Stage 4)
 * @property {object | null} geometry             // GeometryReport (Stage 2); see oracleApi
 * @property {boolean} is_owned_by_caller
 *
 * @typedef {Object} ResignProcessedUrlResponse
 * @property {"success"} status
 * @property {string} processed_image_url
 * @property {string} thumbnail_url
 *
 * @typedef {("UNAUTHENTICATED"|"NOT_FOUND"|"FORBIDDEN"|"INVALID_INPUT"|"INTERNAL")} VaultErrorCode
 *
 * @typedef {Object} VaultErrorResponse
 * @property {"error"} status
 * @property {VaultErrorCode} error_code
 * @property {string} error_message
 */

export const VAULT_ERROR_CODES = Object.freeze({
  UNAUTHENTICATED: "UNAUTHENTICATED",
  NOT_FOUND: "NOT_FOUND",
  FORBIDDEN: "FORBIDDEN",
  INVALID_INPUT: "INVALID_INPUT",
  INTERNAL: "INTERNAL",
});

/** @param {unknown} obj @returns {obj is ListVaultEntriesResponse} */
export function isListVaultEntriesResponse(obj) {
  return Boolean(
    obj && typeof obj === "object"
      && /** @type {any} */ (obj).status === "success"
      && Array.isArray(/** @type {any} */ (obj).entries),
  );
}

/** @param {unknown} obj @returns {obj is VaultEntryDetail} */
export function isVaultEntryDetail(obj) {
  return Boolean(
    obj && typeof obj === "object"
      && /** @type {any} */ (obj).status === "success"
      && /** @type {any} */ (obj).scan_id
      && /** @type {any} */ (obj).verdict,
  );
}

/** @param {unknown} obj @returns {obj is VaultErrorResponse} */
export function isVaultErrorResponse(obj) {
  return Boolean(
    obj && typeof obj === "object"
      && /** @type {any} */ (obj).status === "error"
      && VAULT_ERROR_CODES[/** @type {any} */ (obj).error_code],
  );
}
